import { useState, useCallback, useEffect, useRef } from 'react'
import { RefreshCw, Loader2, AlertCircle, X, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Dices, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { AvatarPreview } from './AvatarPreview'
import { AvatarSelectGroup } from './AvatarSelectGroup'
import {
  buildPromptFromSelections,
  getConstrainedOptions,
  sanitizeSelections,
  randomizeConstrainedSelections,
  type AvatarSelections,
} from './avatarOptions'

const DICE_ICONS: LucideIcon[] = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6]
const DICE_ROLL_INTERVAL_MS = 180

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function pickDifferentDiceIndex(previous: number): number {
  let next = Math.floor(Math.random() * DICE_ICONS.length)
  if (next === previous) next = (next + 1) % DICE_ICONS.length
  return next
}

export interface AvatarGeneratorProps {
  onSave?: (imageData: string, mimeType: string) => void
  /** Fires every time a new image is generated, even before the user clicks "Salvar". */
  onImageGenerated?: (imageData: string, mimeType: string) => void
  /** Compact mode for embedding inside modals — smaller preview, tighter layout */
  compact?: boolean
  disabled?: boolean
  initialImageUrl?: string | null
  previewLoading?: boolean
  previewAlt?: string
  onLoadingChange?: (loading: boolean) => void
  /** Hide the explicit "Salvar" button — used when the parent saves on Continue. */
  hideSaveButton?: boolean
}

const GROUPS = [
  { label: 'Aparência', keys: ['gender', 'skinTone', 'eyeColor', 'expression'] },
  { label: 'Cabelo', keys: ['hairStyle', 'hairColor', 'facialHair'] },
  { label: 'Vestuário', keys: ['clothing', 'glasses', 'headgear', 'accessories'] },
]

const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a']

import { AVATAR_CATEGORIES } from './avatarOptions'

const categoriesByKey = Object.fromEntries(AVATAR_CATEGORIES.map((c) => [c.key, c]))

export function AvatarGenerator({
  onSave,
  onImageGenerated,
  compact = false,
  disabled = false,
  initialImageUrl,
  previewLoading = false,
  previewAlt,
  onLoadingChange,
  hideSaveButton = false,
}: AvatarGeneratorProps) {
  const [selections, setSelections] = useState<AvatarSelections>({})
  const [imageData, setImageData] = useState<string | null>(null)
  const [mimeType, setMimeType] = useState('image/png')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customized, setCustomized] = useState(false)
  const [diceIndex, setDiceIndex] = useState<number | null>(null)
  const [secretUnlocked, setSecretUnlocked] = useState(false)
  const [secretText, setSecretText] = useState('')
  const konamiIndex = useRef(0)

  // Roll dice icon while a generation is in progress.
  useEffect(() => {
    if (!isLoading) {
      setDiceIndex(null)
      return
    }
    if (prefersReducedMotion()) return

    let current = -1
    setDiceIndex(() => {
      current = pickDifferentDiceIndex(current)
      return current
    })
    const id = window.setInterval(() => {
      setDiceIndex(() => {
        current = pickDifferentDiceIndex(current)
        return current
      })
    }, DICE_ROLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [isLoading])

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return

      if (e.key === KONAMI[konamiIndex.current]) {
        konamiIndex.current++
        if (konamiIndex.current === KONAMI.length) {
          setSecretUnlocked(true)
          konamiIndex.current = 0
        }
      } else {
        konamiIndex.current = 0
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  const hasSelections = Object.keys(selections).length > 0
  const previewIsLoading = isLoading || (!imageData && !initialImageUrl && previewLoading)

  const handleSelect = useCallback((key: string, value: string) => {
    setSelections((prev) => sanitizeSelections({ ...prev, [key]: value }))
    setCustomized(true)
  }, [])

  const generateFromSelections = useCallback(async (current: AvatarSelections) => {
    if (disabled || isLoading) return
    if (Object.keys(current).length === 0) return

    setIsLoading(true)
    setError(null)

    const prompt = secretText.trim()
      ? secretText.trim()
      : buildPromptFromSelections(current)

    try {
      const response = await fetch('/dashboard/avatar_generator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ prompt }),
      })

      const data = await response.json()

      if (data.success) {
        const nextMime = data.mime_type || 'image/png'
        setImageData(data.image_data)
        setMimeType(nextMime)
        onImageGenerated?.(data.image_data, nextMime)
      } else {
        setError(data.error || 'Erro ao gerar avatar. Tente novamente.')
      }
    } catch {
      setError('Erro de conexão. Verifique sua internet.')
    } finally {
      setIsLoading(false)
    }
  }, [csrfToken, secretText, disabled, isLoading, onImageGenerated])

  const handleGenerate = useCallback(() => {
    if (!hasSelections) return
    void generateFromSelections(selections)
  }, [generateFromSelections, hasSelections, selections])

  const handleShuffleAndGenerate = useCallback(() => {
    if (disabled || isLoading) return
    const next = randomizeConstrainedSelections()
    setSelections(next)
    setCustomized(false)
    void generateFromSelections(next)
  }, [disabled, isLoading, generateFromSelections])

  return (
    <div className={cn('flex flex-col items-center max-w-4xl mx-auto', compact ? 'gap-4' : 'gap-6')}>
      {/* Avatar preview */}
      <AvatarPreview
        imageData={imageData}
        imageUrl={imageData ? null : initialImageUrl}
        mimeType={mimeType}
        isLoading={previewIsLoading}
        size={compact ? 140 : 280}
        compact={compact}
        alt={previewAlt}
        testId={previewIsLoading ? 'avatar-generator-preview-loading' : 'avatar-generator-preview'}
      />

      {/* Top action buttons — only in full mode */}
      {!compact && (
        <>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <ShuffleAvatarButton
              onClick={handleShuffleAndGenerate}
              rolling={isLoading && !customized}
              diceIndex={diceIndex}
              disabled={disabled || isLoading}
            />
            {imageData && onSave && (
              <Button onClick={() => onSave(imageData, mimeType)} disabled={disabled}>
                Salvar
              </Button>
            )}
          </div>

          <hr className="w-full border-border" />
        </>
      )}

      {/* 3-column groups grid */}
      <div className={cn('grid grid-cols-1 md:grid-cols-3 w-full', compact ? 'gap-x-4 gap-y-2' : 'gap-6')}>
        {GROUPS.map((group) => (
          <div key={group.label} className={cn('flex flex-col', compact ? 'gap-2' : 'gap-4')}>
            <span className="text-sm font-semibold text-foreground">
              {group.label}
            </span>
            {group.keys.map((key) => {
              const category = categoriesByKey[key]
              if (!category) return null
              const isHairColorDisabled = key === 'hairColor' && selections.hairStyle === 'Bald'
              const isEyeColorDisabled = key === 'eyeColor' && selections.glasses === 'Sunglasses'
              return (
                <AvatarSelectGroup
                  key={key}
                  label={category.label}
                  options={getConstrainedOptions(key, selections)}
                  selected={selections[key]}
                  onSelect={(value) => handleSelect(key, value)}
                  disabled={disabled || isHairColorDisabled || isEyeColorDisabled}
                />
              )
            })}
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive" className="relative w-full">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <button
            type="button"
            onClick={() => setError(null)}
            className="absolute top-3 right-3 text-destructive/70 hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* Bottom action buttons — two CTAs side by side, save lives elsewhere when hidden */}
      {compact ? (
        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <ShuffleAvatarButton
            onClick={handleShuffleAndGenerate}
            rolling={isLoading && !customized}
            diceIndex={diceIndex}
            disabled={disabled || isLoading}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleGenerate}
            disabled={disabled || isLoading || !hasSelections}
            className="flex-1 transition-all hover:-translate-y-[1px] hover:shadow-md"
          >
            {isLoading && customized ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Gerar com essas escolhas
          </Button>
          {imageData && onSave && !hideSaveButton && (
            <Button onClick={() => onSave(imageData, mimeType)} disabled={disabled} className="sm:w-32">
              Salvar
            </Button>
          )}
        </div>
      ) : (
        <div className="w-full">
          <Button
            size="lg"
            className="w-full"
            onClick={handleGenerate}
            disabled={disabled || isLoading || !hasSelections}
          >
            {isLoading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Gerar com essas escolhas
          </Button>
        </div>
      )}

      {/* Secret input — revealed by Konami code */}
      <div
        className={cn(
          'w-full overflow-hidden transition-all duration-500 ease-in-out',
          secretUnlocked ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <Input
          placeholder="Modo secreto desbloqueado... descreva o que quiser"
          value={secretText}
          onChange={(e) => setSecretText(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

interface ShuffleAvatarButtonProps {
  onClick: () => void
  rolling: boolean
  diceIndex: number | null
  disabled?: boolean
  className?: string
}

function ShuffleAvatarButton({ onClick, rolling, diceIndex, disabled, className }: ShuffleAvatarButtonProps) {
  const ActiveDice = diceIndex !== null ? DICE_ICONS[diceIndex] : null
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative overflow-hidden transition-all duration-200',
        'hover:-translate-y-[1px] hover:shadow-md',
        rolling && 'ring-2 ring-violet-500/40',
        className,
      )}
      aria-label="Sortear novo avatar"
    >
      <span className="relative inline-flex h-4 w-4 items-center justify-center">
        {ActiveDice ? (
          <ActiveDice
            key={diceIndex}
            className="absolute inset-0 h-4 w-4 text-violet-500 animate-dice-tumble drop-shadow-[0_0_6px_rgba(139,92,246,0.55)]"
          />
        ) : (
          <Dices className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12 group-hover:text-violet-500" />
        )}
      </span>
      Sortear novo avatar
    </Button>
  )
}
